class BeforeAfterSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.sliderWrapper = null;
    this.beforeImage = null;
    this.afterImage = null;
    this.sliderLine = null;
    this.sliderHandler = null;
    this.pointerDownHandler = null;
    this.pointerMoveHandler = null;
    this.pointerUpHandler = null;
    this.dragStartHandler = null;
    this.sectionLoadHandler = null;
    this.isDragging = false;
  }

  connectedCallback() {
    this.init();
    this.bindSectionLoad();
  }

  disconnectedCallback() {
    this.unbindSectionLoad();
    this.unbindSlider();
    this.unbindPointerDrag();
  }

  init() {
    this.slider = this.querySelector('.before-and-after__range');
    this.sliderWrapper = this.querySelector('.before-and-after__slider-wrapper');
    this.beforeImage = this.querySelector('.before-and-after__before');
    this.afterImage = this.querySelector('.before-and-after__after');
    this.sliderLine = this.querySelector('.before-and-after__slider-line');

    if (!this.slider || !this.sliderWrapper || !this.beforeImage || !this.afterImage || !this.sliderLine) {
      console.warn('Before/After Slider: Required elements not found');
      return;
    }

    this.unbindSlider();
    this.unbindPointerDrag();

    this.sliderHandler = () => {
      this.moveSlider();
    };

    this.slider.addEventListener('input', this.sliderHandler);

    this.pointerDownHandler = (event) => this.onPointerDown(event);
    this.pointerMoveHandler = (event) => this.onPointerMove(event);
    this.pointerUpHandler = (event) => this.onPointerUp(event);
    this.dragStartHandler = (event) => this.onDragStart(event);

    this.sliderWrapper.addEventListener('pointerdown', this.pointerDownHandler);
    this.sliderWrapper.addEventListener('pointermove', this.pointerMoveHandler);
    this.sliderWrapper.addEventListener('pointerup', this.pointerUpHandler);
    this.sliderWrapper.addEventListener('pointercancel', this.pointerUpHandler);
    this.sliderWrapper.addEventListener('dragstart', this.dragStartHandler);

    this.moveSlider();
  }

  isInteractiveTarget(target) {
    return Boolean(target.closest('a, button, .before-and-after__button'));
  }

  onDragStart(event) {
    if (!this.isInteractiveTarget(event.target)) {
      event.preventDefault();
    }
  }

  onPointerDown(event) {
    if (this.isInteractiveTarget(event.target)) return;
    if (event.button !== undefined && event.button !== 0) return;

    event.preventDefault();

    this.isDragging = true;
    this.sliderWrapper.setPointerCapture(event.pointerId);
    this.sliderWrapper.classList.add('is-dragging');
    this.updateSliderFromPointer(event);
  }

  onPointerMove(event) {
    if (!this.isDragging) return;

    event.preventDefault();
    this.updateSliderFromPointer(event);
  }

  onPointerUp(event) {
    if (!this.isDragging) return;

    this.isDragging = false;

    if (this.sliderWrapper.hasPointerCapture(event.pointerId)) {
      this.sliderWrapper.releasePointerCapture(event.pointerId);
    }

    this.sliderWrapper.classList.remove('is-dragging');
  }

  updateSliderFromPointer(event) {
    const rect = this.sliderWrapper.getBoundingClientRect();
    let percent = ((event.clientX - rect.left) / rect.width) * 100;

    if (percent <= 0) percent = 0;
    if (percent >= 100) percent = 100;

    this.slider.value = percent;
    this.moveSlider();
  }

  bindSectionLoad() {
    this.unbindSectionLoad();

    this.sectionLoadHandler = (event) => {
      if (event.detail && event.detail.sectionId) {
        const section = document.getElementById(`shopify-section-${event.detail.sectionId}`);

        if (section && section.contains(this)) {
          this.init();
        }
      } else {
        this.init();
      }
    };

    document.addEventListener('shopify:section:load', this.sectionLoadHandler);
  }

  unbindSectionLoad() {
    if (this.sectionLoadHandler) {
      document.removeEventListener('shopify:section:load', this.sectionLoadHandler);
      this.sectionLoadHandler = null;
    }
  }

  unbindSlider() {
    if (this.slider && this.sliderHandler) {
      this.slider.removeEventListener('input', this.sliderHandler);
      this.sliderHandler = null;
    }
  }

  unbindPointerDrag() {
    if (!this.sliderWrapper) return;

    if (this.pointerDownHandler) {
      this.sliderWrapper.removeEventListener('pointerdown', this.pointerDownHandler);
      this.pointerDownHandler = null;
    }

    if (this.pointerMoveHandler) {
      this.sliderWrapper.removeEventListener('pointermove', this.pointerMoveHandler);
      this.pointerMoveHandler = null;
    }

    if (this.pointerUpHandler) {
      this.sliderWrapper.removeEventListener('pointerup', this.pointerUpHandler);
      this.sliderWrapper.removeEventListener('pointercancel', this.pointerUpHandler);
      this.pointerUpHandler = null;
    }

    if (this.dragStartHandler) {
      this.sliderWrapper.removeEventListener('dragstart', this.dragStartHandler);
      this.dragStartHandler = null;
    }
  }

  moveSlider() {
    if (!this.slider || !this.beforeImage || !this.afterImage || !this.sliderLine) {
      return;
    }

    const value = this.slider.value;

    this.beforeImage.style.clipPath = `inset(0 ${100 - value}% 0 0)`;
    this.afterImage.style.clipPath = `inset(0 0 0 ${value}%)`;
    this.sliderLine.style.setProperty('--slider-position', `${value}%`);
  }
}

customElements.define('before-after-slider', BeforeAfterSlider);
